import {
  StoreMetaDataApi,
  IPurchaseFlowProperties,
  EcommerceSettings,
  EcommerceSettingsService as EcommerceSettingsServiceSdk,
} from '@wix/wixstores-client-storefront-sdk';
import {PAYPAL_METHOD_NAME, PaymentMethodType} from '../../common/constants';
import {PaymentMethod} from '@wix/wixstores-graphql-schema-node';
import {getPayableCurrencies} from '@wix/ambassador-cashier-pay-v2-account/http';
import type {SiteStore} from '@wix/wixstores-client-storefront-sdk';
import type {ControllerFlowAPI} from '@wix/yoshi-flow-editor';
import {SPECS} from '../specs';

export class EcommerceSettingsService {
  private readonly ecommerceSettingsServiceSdk: EcommerceSettingsServiceSdk;
  private readonly purchaseFlowPropertiesPromise?: Promise<IPurchaseFlowProperties>;
  private ecommerceSettings?: EcommerceSettings;
  private readonly siteStore: SiteStore;
  private readonly flowAPI: ControllerFlowAPI;
  public hasCreatedPaymentMethods: boolean;

  constructor({
    ecommerceSettingsServiceSdk,
    siteStore,
    flowAPI,
  }: {
    ecommerceSettingsServiceSdk: EcommerceSettingsServiceSdk;
    siteStore: SiteStore;
    flowAPI: ControllerFlowAPI;
  }) {
    this.ecommerceSettingsServiceSdk = ecommerceSettingsServiceSdk;
    this.purchaseFlowPropertiesPromise = this.ecommerceSettingsServiceSdk.getPurchaseFlowProperties();
    this.siteStore = siteStore;
    this.flowAPI = flowAPI;
  }

  public async get(): Promise<ReturnType<StoreMetaDataApi['getStoreMetaData']>> {
    /* istanbul ignore else */
    if (this.purchaseFlowPropertiesPromise !== undefined) {
      return (await this.purchaseFlowPropertiesPromise).storeMetaData;
    }
  }

  public async init(options?: {shouldWarmupData: boolean}) {
    try {
      this.ecommerceSettings = await this.ecommerceSettingsServiceSdk.getEcommerceSettings(options);
    } catch {
      this.ecommerceSettings = undefined;
    }
  }
  public async initPayableCurrencies() {
    try {
      if (this.siteStore.experiments.enabled(SPECS.UsePayableCurrencies)) {
        const payableCurrencies =
          (
            await this.flowAPI.errorHandler.withErrorHandler(
              () => this.flowAPI.essentials.httpClient.request(getPayableCurrencies({})),
              {errorCodesMap: {}}
            )
          ).data.currencyCode ?? /* istanbul ignore next */ [];
        this.hasCreatedPaymentMethods = payableCurrencies.length > 0;
      }
    } catch {
      this.hasCreatedPaymentMethods = false;
    }
  }

  public getEcommerceSettings() {
    return this.ecommerceSettings ?? {hasValidationsInCart: true, hasAdditionalFees: true};
  }

  private async getActivePaymentMethods() {
    const storeMetaData = await this.get();
    return storeMetaData?.activePaymentMethods ?? /* istanbul ignore next */ [];
  }

  private async getRegularFlowPaymentMethods(): Promise<PaymentMethod[]> {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.filter(({name}) => name !== PAYPAL_METHOD_NAME);
  }

  private async getEWalletPaymentMethods(): Promise<PaymentMethod[]> {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.filter((paymentMethod) => paymentMethod.type === PaymentMethodType.eWallet);
  }

  private async getOfflinePaymentMethods(): Promise<PaymentMethod[]> {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.filter((paymentMethod) => paymentMethod.type === PaymentMethodType.offline);
  }

  public async hasRegularFlowPaymentMethods() {
    return !!(await this.getRegularFlowPaymentMethods()).length;
  }

  public async hasAnyPaymentMethods() {
    return !!(await this.getActivePaymentMethods()).length;
  }

  public async hasEWalletPaymentMethods() {
    return !!(await this.getEWalletPaymentMethods()).length;
  }

  public async hasOfflinePaymentMethods() {
    return !!(await this.getOfflinePaymentMethods()).length;
  }

  public async getPaymentMethodsNames() {
    const activePaymentMethods = await this.getActivePaymentMethods();
    return activePaymentMethods.map((method) => method.name);
  }
}
